import { FC } from 'react'
import useHover from 'hooks/useHover'
import { Portal } from '@radix-ui/react-dialog'
import { AddressData } from '@wellesley/openapi'
import { FormProvider, useForm } from 'react-hook-form'
import { MOBILE_POST_FORM_LOCATION_INPUT_PORTAL_ID } from 'components/mobile/MobilePostForm'
import {
  cn,
  Button,
  Popover,
  PopoverContent,
  PopoverAnchor,
  useIsMobile,
} from '@wellesley/ui'

import Divider from './components/Divider'
import SearchLoader from './components/SearchLoader'
import LocationInput from './components/LocationInput'
import SuggestionList from './components/SuggestionList'
import LocationToggler from './components/LocationToggler'
import AutoDetectSwitch from './components/AutoDetectSwitch'
import useLocationSelector from './hooks/useLocationSelector'

export type LocationSelectorProps = {
  location: AddressData | null
  className?: string
  isOpenLocationInput: boolean
  setLocation: (this: void, value: AddressData) => void
  clearLocation: (this: void) => void
  toggleOpenLocationInput: (this: void) => void
}

const LocationSelector: FC<LocationSelectorProps> = (props) => {
  const { location, className, isOpenLocationInput, toggleOpenLocationInput } =
    props
  const methods = useForm()
  const isMobile = useIsMobile()
  const [ref, isHovered, setHovered] = useHover<HTMLDivElement>()

  const {
    inputValue,
    inputRef,
    anchorRef,
    suggestions,
    isLoadingSuggestions,
    isAutoDetecting,
    autoDetect,
    hasSuggestions,
    showDropdown,
    isSaveDisabled,
    handleInputChange,
    handleInputFocus,
    handleSelectSuggestion,
    handleClearInput,
    handleAutoDetectToggle,
    handleSave,
    handleIconCrossClick,
    setIsDropdownOpen,
  } = useLocationSelector({ ...props })

  const hasCloseButton =
    (!!location && isHovered && !isOpenLocationInput) ||
    (isMobile && isOpenLocationInput)

  const handleCrossClick = (e: React.MouseEvent<SVGSVGElement>) => {
    handleIconCrossClick(e)
    setHovered(false)
  }

  const handleAnchorRef = (node: HTMLDivElement | null) => {
    anchorRef.current = node
  }

  const handleOpenAutoFocus = (e: Event) => {
    e.preventDefault()
  }

  const handleInteractOutside = (e: Event) => {
    const target = (e as CustomEvent).target as Node | null
    if (anchorRef.current?.contains(target)) return
    setIsDropdownOpen(false)
  }

  const popoverContent = (
    <div className="duration-custom relative flex-1 transition-opacity">
      <Popover open={showDropdown}>
        <PopoverAnchor asChild>
          <LocationInput
            ref={inputRef}
            value={inputValue}
            anchorRef={handleAnchorRef}
            onChange={handleInputChange}
            onFocus={handleInputFocus}
            onClear={handleClearInput}
          />
        </PopoverAnchor>

        <PopoverContent
          align="start"
          sideOffset={8}
          className="!p-xxs z-[101] w-[var(--radix-popover-trigger-width)] !gap-0.5"
          onOpenAutoFocus={handleOpenAutoFocus}
          onInteractOutside={handleInteractOutside}
        >
          <AutoDetectSwitch
            checked={autoDetect}
            isDetecting={isAutoDetecting}
            onCheckedChange={handleAutoDetectToggle}
          />

          {hasSuggestions && <Divider />}

          {isLoadingSuggestions && <SearchLoader />}

          {!isLoadingSuggestions && hasSuggestions && (
            <SuggestionList
              suggestions={suggestions}
              onSelect={handleSelectSuggestion}
            />
          )}
        </PopoverContent>
      </Popover>
    </div>
  )

  const isLoacationTogglerHighlighted =
    !!location || (isMobile && isOpenLocationInput)

  return (
    <FormProvider {...methods}>
      <div
        className={cn(className, isOpenLocationInput ? 'flex-1' : '')}
        data-testid="location-selector"
      >
        <div className="gap-xxs flex flex-1 items-center max-md:justify-between">
          <LocationToggler
            ref={ref}
            isHighlighted={isLoacationTogglerHighlighted}
            hasCloseButton={hasCloseButton}
            onToggle={toggleOpenLocationInput}
            onCrossClick={handleCrossClick}
          />

          {isOpenLocationInput ? (
            isMobile ? (
              <Portal
                container={document.getElementById(
                  MOBILE_POST_FORM_LOCATION_INPUT_PORTAL_ID,
                )}
              >
                {popoverContent}
              </Portal>
            ) : (
              popoverContent
            )
          ) : null}

          {isOpenLocationInput && (
            <Button
              variant="primary"
              className="w-[80px]"
              disabled={!!isSaveDisabled}
              onClick={handleSave}
              data-testid="location-save-button"
            >
              Save
            </Button>
          )}
        </div>
      </div>
    </FormProvider>
  )
}

export default LocationSelector
